// Contas fixas: seguro, rastreador, financiamento, tag… Todo mês o HSA Frota
// lança a despesa (a prazo, com vencimento no dia combinado), que aparece em
// "A pagar". Quem lança é o servidor, quando há sinal.
import { categorias, contasFixas, despesas, parcelas, situacoesDeEnvio, veiculos } from '../dados.js';
import { somarMeses } from '../lib/despesas.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, mesCurto, mesPorExtenso, numeroBR, placa as formatarPlaca } from '../lib/formato.js';
import { estado } from '../local/sincronia.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { opcoesDeCategoria, opcoesDeVeiculo } from './despesas.js';
import { faixaDaConta } from './inicio.js';

// Meses entre duas competências (AAAA-MM-01): b − a.
function mesesEntre(a, b) {
  const [ya, ma] = a.split('-').map(Number);
  const [yb, mb] = b.split('-').map(Number);
  return (yb - ya) * 12 + (mb - ma);
}

// Em que parcela a conta parcelada está neste mês (ou null).
function parcelaDoMes(conta, hoje) {
  if (conta.tipo !== 'parcelado') return null;
  const n = mesesEntre(conta.primeira_competencia, `${hoje.slice(0, 7)}-01`) + 1;
  return { numero: Math.max(0, Math.min(n, conta.total_parcelas)), total: conta.total_parcelas, terminou: n > conta.total_parcelas };
}

// ---------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------
export function telaContasFixas() {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, cats, frota, envio] = await Promise.all([
      contasFixas.listar(), categorias.todas(), veiculos.listar(), situacoesDeEnvio('contas_fixas'),
    ]);
    const hoje = hojeDaEmpresa();
    const mesAtual = `${hoje.slice(0, 7)}-01`;
    const gravavel = podeGravar();
    const encerrada = (c) => Boolean(c.encerrada_em) || Boolean(parcelaDoMes(c, hoje)?.terminou);
    const ativas = lista.filter((c) => !encerrada(c));
    const outras = lista.filter(encerrada);

    const cartao = (c) => {
      const cat = cats.find((x) => x.id === c.categoria_id);
      const veic = frota.find((v) => v.id === c.veiculo_id);
      const p = parcelaDoMes(c, hoje);
      return cartaoLink({
        href: `#/contas-fixas/${c.id}`,
        simbolo: 'calendario',
        titulo: c.descricao,
        linhas: [
          `${dinheiro(c.valor)} por mês · vence dia ${c.dia_vencimento}`,
          `${cat?.nome ?? ''}${veic ? ` · ${formatarPlaca(veic.placa)}` : ' · Geral'}`,
        ],
        emblemas: [
          c.primeira_competencia > mesAtual
            ? emblema(`Começa em ${mesCurto(c.primeira_competencia)}`, 'info')
            : p && !p.terminou && emblema(`Parcela ${p.numero} de ${p.total}`, 'info'),
          encerrada(c) && emblema(p?.terminou ? 'Terminou' : `Encerrada em ${data(c.encerrada_em)}`),
          emblemaEnvio(envio.get(c.id)),
        ],
      });
    };

    if (lista.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'calendario',
        titulo: 'Nenhuma conta fixa',
        texto: 'Seguro, rastreador, financiamento, tag, contador… Cadastre uma vez: todo mês o HSA Frota lança a despesa e ela aparece em "A pagar".',
        acao: gravavel && h('a', { class: 'botao', href: '#/contas-fixas/nova' }, 'Cadastrar conta fixa'),
      }));
      return;
    }

    const porMes = ativas.reduce((s, c) => s + Math.round(Number(c.valor) * 100), 0) / 100;
    trocar(corpo,
      gravavel && h('a', { class: 'botao', href: '#/contas-fixas/nova' }, '+ Conta fixa'),
      secao('Ativas', h('div', { class: 'lista' },
        ativas.map(cartao),
        ativas.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma conta ativa.')), { contador: `${dinheiro(porMes)} por mês` }),
      outras.length > 0 && secao('Encerradas', h('div', { class: 'lista' }, outras.map(cartao))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Contas fixas', voltar: '/mais', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Cadastro, edição e as despesas lançadas
// ---------------------------------------------------------------------
export function telaContaFixa({ params }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? 'Nova conta fixa' : 'Conta fixa');

  (async () => {
    const conta = nova ? null : await contasFixas.obter(params.id);
    if (!nova && !conta) {
      trocar(corpo, vazio({ titulo: 'Conta não encontrada', texto: 'Ela pode ter sido excluída.', acao: h('a', { class: 'botao', href: '#/contas-fixas' }, 'Ver contas fixas') }));
      return;
    }
    const [cats, todasCats, frota, lancadas, todasParcelas] = await Promise.all([
      categorias.listar(), categorias.todas(), veiculos.listar(),
      conta ? despesas.daContaFixa(conta.id) : [], conta ? parcelas.listar() : [],
    ]);
    const hoje = hojeDaEmpresa();
    const mesAtual = `${hoje.slice(0, 7)}-01`;
    const gravavel = podeGravar();
    if (conta) titulo.textContent = conta.descricao;
    const escolhiveis = conta && !cats.some((c) => c.id === conta.categoria_id)
      ? [...cats, todasCats.find((c) => c.id === conta.categoria_id)].filter(Boolean)
      : cats;

    // Primeiro mês: de 2 anos atrás a 1 ano à frente.
    const meses = [];
    for (let i = -24; i <= 12; i++) meses.push(somarMeses(mesAtual, i));
    if (conta && !meses.includes(conta.primeira_competencia)) meses.push(conta.primeira_competencia);
    meses.sort();

    const dicaMes = h('p', { class: 'dica', 'aria-live': 'polite' });
    const campoTotal = campo({
      rotulo: 'Total de parcelas', nome: 'total_parcelas', formato: 'dias', valor: conta?.total_parcelas ?? '',
      atributos: { inputmode: 'numeric' }, aoMudar: () => atualizar(),
    });

    const form = formulario({
      filhos: [
        campo({ rotulo: 'Descrição', nome: 'descricao', obrigatorio: true, valor: conta?.descricao ?? '', dica: 'Como aparece em "A pagar": "Seguro do Scania", "Rastreador"…' }),
        campo({
          rotulo: 'Categoria', nome: 'categoria_id', tipo: 'select', obrigatorio: true, valor: conta?.categoria_id ?? '',
          opcoes: [['', 'Escolha a categoria'], ...opcoesDeCategoria(escolhiveis, { primeiro: 'fixo' })],
        }),
        campo({
          rotulo: 'Veículo', nome: 'veiculo_id', tipo: 'select', obrigatorio: true, valor: conta ? conta.veiculo_id ?? 'geral' : '',
          opcoes: opcoesDeVeiculo(frota, conta?.veiculo_id),
          dica: 'A despesa entra no custo deste veículo. Conta da empresa toda (contador, sistema…): "Nenhum".',
        }),
        h('div', { class: 'grade-2' },
          campo({
            rotulo: 'Valor (R$)', nome: 'valor', obrigatorio: true, formato: 'dinheiro',
            valor: conta ? numeroBR(conta.valor, { casas: 2 }) : '', atributos: { inputmode: 'decimal' },
          }),
          campo({
            rotulo: 'Vence todo dia', nome: 'dia_vencimento', obrigatorio: true, formato: 'dia', valor: conta?.dia_vencimento ?? '',
            atributos: { inputmode: 'numeric' },
          })),
        campo({
          rotulo: 'Tipo', nome: 'tipo', tipo: 'segmentos', valor: conta?.tipo ?? 'mensal',
          opcoes: [['mensal', 'Todo mês', 'até encerrar'], ['parcelado', 'Parcelado', 'número certo de meses']],
          aoMudar: () => atualizar(),
        }),
        campoTotal,
        campo({
          rotulo: 'Primeiro mês', nome: 'primeira_competencia', tipo: 'select', obrigatorio: true,
          valor: conta?.primeira_competencia ?? mesAtual,
          opcoes: meses.map((m) => [m, mesPorExtenso(m)]),
          aoMudar: () => atualizar(),
        }),
        dicaMes,
      ],
      textoBotao: nova ? 'Salvar conta fixa' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (d.descricao.length < 2) erros.descricao = 'Descreva a conta (ex.: Seguro do Scania).';
        const cat = todasCats.find((c) => c.id === d.categoria_id);
        if (!cat) erros.categoria_id = 'Escolha a categoria.';
        const veic = frota.find((v) => v.id === d.veiculo_id);
        if (!d.veiculo_id) erros.veiculo_id = 'Escolha o veículo (ou "Nenhum", se for conta da empresa toda).';
        else if (veic?.tipo === 'implemento' && cat && !cat.permite_implemento) erros.veiculo_id = `${cat.nome} não é lançado na carreta: escolha o caminhão.`;
        if (!(lerNumero(d.valor) > 0)) erros.valor = 'Informe o valor, como 850,00.';
        const dia = lerInteiro(d.dia_vencimento);
        if (!(dia >= 1 && dia <= 31)) erros.dia_vencimento = 'Um dia de 1 a 31.';
        if (d.tipo === 'parcelado') {
          const n = lerInteiro(d.total_parcelas);
          if (!(n >= 1 && n <= 360)) erros.total_parcelas = 'De 1 a 360 parcelas.';
        }
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          descricao: d.descricao,
          categoria_id: d.categoria_id,
          veiculo_id: d.veiculo_id === 'geral' ? null : d.veiculo_id,
          valor: lerNumero(d.valor),
          dia_vencimento: lerInteiro(d.dia_vencimento),
          tipo: d.tipo,
          total_parcelas: d.tipo === 'parcelado' ? lerInteiro(d.total_parcelas) : null,
          primeira_competencia: d.primeira_competencia,
        };
        if (nova) {
          await contasFixas.criar(dados);
          avisar(estado.online
            ? 'Conta fixa salva. A despesa do mês aparece em "A pagar" em instantes.'
            : 'Conta fixa salva. A despesa do mês é lançada quando o sinal voltar.');
          tela.ir('/contas-fixas', { substituir: true });
          return;
        }
        await contasFixas.atualizar(conta.id, dados, conta);
        avisar('Alterações salvas. Valem para os próximos meses.');
        tela.renderizar();
      },
      extras: !nova && gravavel && [
        conta.encerrada_em
          ? h('button', {
            class: 'botao cheio secundario',
            type: 'button',
            onClick: () => mudar(() => contasFixas.atualizar(conta.id, { encerrada_em: null }), 'A conta voltou a ser lançada todo mês.'),
          }, 'Voltar a lançar todo mês')
          : h('button', {
            class: 'botao cheio secundario',
            type: 'button',
            onClick: async () => {
              const ok = await confirmar({
                titulo: 'Encerrar esta conta?',
                texto: `O último mês lançado é ${mesPorExtenso(hoje)}. As despesas já lançadas continuam como estão.`,
                sim: 'Encerrar',
              });
              if (ok) mudar(() => contasFixas.atualizar(conta.id, { encerrada_em: hoje }), 'Conta encerrada.');
            },
          }, 'Encerrar conta (não lançar mais)'),
        h('button', {
          class: 'botao texto',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({
              titulo: 'Excluir esta conta fixa?',
              texto: 'Use só para conta cadastrada por engano. Ela vai para a lixeira e não lança mais nada; as despesas já lançadas ficam (exclua uma a uma, se precisar).',
              sim: 'Excluir',
              perigo: true,
            });
            if (!ok) return;
            try {
              await contasFixas.excluir(conta.id);
              avisar('Conta excluída.');
              tela.ir('/contas-fixas', { substituir: true });
            } catch (erro) {
              avisar(mensagemDeErro(erro), 'erro');
            }
          },
        }, 'Excluir conta cadastrada por engano'),
      ],
    });

    async function mudar(acao, sucesso) {
      try {
        await acao();
        avisar(sucesso);
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    }

    function atualizar() {
      const tipo = form.querySelector('[name="tipo"]:checked')?.value;
      campoTotal.hidden = tipo !== 'parcelado';
      const primeira = form.querySelector('[name="primeira_competencia"]').value;
      const partes = [];
      const atrasados = mesesEntre(primeira, mesAtual);
      if (atrasados > 0 && (nova || primeira < conta.primeira_competencia)) {
        partes.push(`Serão lançados ${atrasados + 1} meses de uma vez (de ${mesCurto(primeira)} a ${mesCurto(mesAtual)}), todos a pagar. Os que já foram pagos, marque em "A pagar".`);
      }
      const n = lerInteiro(form.querySelector('[name="total_parcelas"]').value);
      if (tipo === 'parcelado' && n >= 1) partes.push(`A última parcela é de ${mesCurto(somarMeses(primeira, n - 1))}.`);
      dicaMes.textContent = partes.join(' ');
      dicaMes.hidden = partes.length === 0;
    }
    atualizar();

    if (nova) {
      trocar(corpo,
        faixa('Todo mês o HSA Frota lança esta conta como despesa a prazo, com vencimento no dia escolhido. Ela aparece em "A pagar" até você marcar como paga.', 'info'),
        form);
      return;
    }

    // Despesas já lançadas por esta conta, do mês mais novo para o mais antigo.
    const parcelaDe = (l) => todasParcelas.find((p) => p.lancamento_id === l.id);
    const linhas = [...lancadas].sort((a, b) => (a.competencia < b.competencia ? 1 : -1)).map((l) => {
      const p = parcelaDe(l);
      const situacao = p?.pago_em ? emblema('Paga', 'ok') : p && p.vencimento < hoje ? emblema('Vencida', 'perigo') : emblema('A pagar', 'alerta');
      return cartaoLink({
        href: `#/despesas/${l.id}?volta=${encodeURIComponent(`/contas-fixas/${conta.id}`)}`,
        simbolo: 'recibo',
        titulo: `${mesPorExtenso(l.competencia ?? l.data)} · ${dinheiro(l.valor)}`,
        linhas: [p?.pago_em ? `Paga em ${data(p.pago_em)}` : `Vence em ${data(p?.vencimento ?? l.data)}`],
        emblemas: [situacao],
      });
    });

    trocar(corpo,
      conta.encerrada_em && faixa(`Encerrada em ${data(conta.encerrada_em)}: não lança mais meses depois de ${mesCurto(conta.encerrada_em)}.`, 'alerta'),
      secao('Despesas lançadas', h('div', { class: 'lista' },
        linhas,
        linhas.length === 0 && h('p', { class: 'pequeno' }, estado.online
          ? 'Nenhuma ainda. A do mês é lançada assim que a conta chegar ao servidor.'
          : 'Nenhuma ainda. A do mês é lançada quando o sinal voltar.')), { contador: String(linhas.length) }),
      secao('Dados da conta', form),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/contas-fixas', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}
